import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { generateCSRFToken, storeCSRFToken, getCSRFToken, rateLimiter } from '@/utils/security';

interface SecurityContextType {
  csrfToken: string | null;
  isRateLimited: (key: string, maxAttempts?: number) => boolean;
  refreshCSRFToken: () => void;
  getRemainingRateLimit: (key: string) => number;
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

export const useSecurityContext = () => {
  const context = useContext(SecurityContext);
  if (context === undefined) {
    throw new Error('useSecurityContext must be used within a SecurityProvider');
  }
  return context;
};

interface SecurityProviderProps {
  children: ReactNode;
}

export const SecurityProvider: React.FC<SecurityProviderProps> = ({ children }) => {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  const refreshCSRFToken = () => {
    const newToken = generateCSRFToken();
    storeCSRFToken(newToken);
    setCsrfToken(newToken);
  };

  const isRateLimited = (key: string, maxAttempts: number = 5) => {
    return !rateLimiter.isAllowed(key, maxAttempts);
  };

  const getRemainingRateLimit = (key: string) => {
    return rateLimiter.getRemainingTime(key);
  };

  useEffect(() => {
    // Initialize or get existing CSRF token
    const existingToken = getCSRFToken();
    if (existingToken) {
      setCsrfToken(existingToken);
    } else {
      refreshCSRFToken();
    }

    // Refresh CSRF token every 30 minutes
    const interval = setInterval(refreshCSRFToken, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const value: SecurityContextType = {
    csrfToken,
    isRateLimited,
    refreshCSRFToken,
    getRemainingRateLimit,
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};

export default SecurityContext;